import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import html2canvas from 'html2canvas';
import './DigitalPoster.css';

function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    api.get('/api/users/profile').then(res => setProfile(res.data.data)).catch(() => setProfile(user));
  }, []);
  return profile || user || {};
}

function useMyBusiness() {
  const [business, setBusiness] = useState(null);
  useEffect(() => {
    api.get('/api/businesses/my').then(res => {
      const list = res.data.data || [];
      const approved = list.find(b => b.status === 'approved') || list[0] || null;
      setBusiness(approved);
    }).catch(() => {});
  }, []);
  return business;
}

const categories = [
  { name: 'Festival', emoji: '📣', bg: '#5bc8c0' },
  { name: 'Business', emoji: '💼', bg: '#7b7fc4' },
  { name: 'Motivational', emoji: '👥', bg: '#e8b84b' },
  { name: 'Social Workers', emoji: '☕', bg: '#e07fa0' },
  { name: 'Trending', emoji: '⭐', bg: '#5bc87a' },
  { name: 'Greetings', emoji: '📅', bg: '#e07a7a' },
  { name: 'Suvichar', emoji: '🖼️', bg: '#5baee8' },
];

// postersByCategory is now loaded from API — see usePostersByCategory hook below

// ── Poster Preview Page ──
function PosterPreviewPage({ poster, category, onBack }) {
  const profile = useProfile();
  const myBusiness = useMyBusiness();
  const fileRef = useRef();
  const today = new Date();
  const dateStr = today.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

  const avatarUrl = profile?.avatar ? `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${profile.avatar}` : null;
  const [profileImg, setProfileImg] = useState(avatarUrl);
  const [name, setName] = useState('');
  const [designation, setDesignation] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [location, setLocation] = useState('');
  const [editing, setEditing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef();

  useEffect(() => {
    if (category === 'Business' && myBusiness) {
      setName(myBusiness.businessName || profile?.name || 'Your Name');
      setDesignation(myBusiness.businessType || profile?.occupation || 'Your Designation');
      setPhone(myBusiness.phone || profile?.phone || '');
      setEmail(myBusiness.email || profile?.email || '');
      setLocation([myBusiness.city, myBusiness.state].filter(Boolean).join(', ') || profile?.address || 'Your City, State');
      if (myBusiness.logo) setProfileImg(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${myBusiness.logo}`);
      else if (profile?.avatar) setProfileImg(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${profile.avatar}`);
    } else {
      setName(profile?.name || 'Your Name');
      setDesignation(profile?.occupation || 'Your Designation');
      setPhone(profile?.phone || '');
      setEmail(profile?.email || '');
      setLocation(profile?.address || 'Your City, State');
      if (profile?.avatar && !profileImg) setProfileImg(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${profile.avatar}`);
    }
  }, [profile, myBusiness, category]);

  const handleImgChange = (e) => {
    const file = e.target.files[0];
    if (file) setProfileImg(URL.createObjectURL(file));
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, { useCORS: true, scale: 2, backgroundColor: null });
      const link = document.createElement('a');
      link.download = `${category}-poster.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch { alert('Download failed. Try again.'); }
    finally { setDownloading(false); }
  };

  const handleShare = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, { useCORS: true, scale: 2, backgroundColor: null });
      canvas.toBlob(async (blob) => {
        if (navigator.share && blob) {
          const file = new File([blob], 'poster.png', { type: 'image/png' });
          await navigator.share({ title: `${category} Poster by ${name}`, files: [file] }).catch(() => {});
        } else {
          const link = document.createElement('a');
          link.download = 'poster.png';
          link.href = canvas.toDataURL();
          link.click();
        }
      });
    } catch { alert('Share failed. Try again.'); }
  };

  return (
    <div className="poster-preview-page">
      <div className="poster-preview-topbar">
        <button className="back-btn" onClick={onBack}>← Back</button>
        <h2>{category} Poster</h2>
      </div>

      <div className="poster-card-wrap">
        {/* ── Poster Image (full, no overlay) ── */}
        <div className="poster-img-box" ref={cardRef}>
          {poster.image
            ? <img src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${poster.image.replace(/\\/g, '/')}`} alt="poster" className="poster-full-img" crossOrigin="anonymous" />
            : <div className="poster-img-placeholder" style={{ background: poster.bg || '#ccc' }} />
          }
          <div className="poster-wm">JustService24</div>
        </div>

        {/* ── User Info Card (below poster) ── */}
        <div className="poster-info-card">
          <div className="poster-info-left">
            <div className="poster-profile-img-wrap" onClick={() => fileRef.current.click()}>
              {profileImg
                ? <img src={profileImg} alt="profile" className="poster-profile-img" />
                : <div className="poster-profile-placeholder">📷<span>Add Photo</span></div>
              }
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImgChange} />
            </div>
          </div>

          <div className="poster-info-col">
            {editing ? (
              <>
                <input className="poster-edit-input name" value={name} onChange={e => setName(e.target.value)} placeholder="Name" />
                <input className="poster-edit-input desig" value={designation} onChange={e => setDesignation(e.target.value)} placeholder="Designation" />
                <input className="poster-edit-input contact" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone" />
                <input className="poster-edit-input contact" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" />
                <input className="poster-edit-input contact" value={location} onChange={e => setLocation(e.target.value)} placeholder="Location" />
                <button className="poster-done-btn" onClick={() => setEditing(false)}>✓ Done</button>
              </>
            ) : (
              <>
                <div className="poster-name-row">
                  <span className="poster-name">{name}</span>
                  {designation && <><span className="poster-divider">|</span><span className="poster-desig">{designation}</span></>}
                </div>
                {(phone || email) && (
                  <div className="poster-contact-row">
                    {phone && <span>📋 {phone}</span>}
                    {email && <span>{email}</span>}
                  </div>
                )}
                {location && <div className="poster-location-row">📍 {location}</div>}
                <button className="poster-edit-btn" onClick={() => setEditing(true)}>✏️ Edit</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Action Buttons ── */}
      <div className="poster-action-btns">
        <button className="poster-btn-download" onClick={handleDownload} disabled={downloading}>
          {downloading ? '⏳ Downloading...' : '⬇️ Download'}
        </button>
        <button className="poster-btn-share" onClick={handleShare}>
          📤 Share
        </button>
      </div>
    </div>
  );
}

// ── Poster List (after category selected) ──
function PosterList({ category, catBg, onSelect, onBack }) {
  const [posters, setPosters] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get(`/api/digital-posters?category=${encodeURIComponent(category)}`)
      .then(res => setPosters(res.data.data || []))
      .catch(() => setPosters([]));
  }, [category]);

  const handleCreate = () => {
    if (!selected) return;
    setCreating(true);
    setTimeout(() => { setCreating(false); onSelect(selected); }, 1800);
  };

  return (
    <div className="poster-page">
      <div className="poster-header" style={{ background: catBg || 'linear-gradient(135deg,#00C853,#00E676)' }}>
        <div className="container">
          <button className="back-btn" onClick={onBack}>← Back</button>
          <h1>{category} Posters</h1>
        </div>
      </div>
      <div className="container">
        <div className="poster-list-grid">
          {posters.length === 0 && <p style={{ color: '#888', textAlign: 'center', padding: '32px 0', gridColumn: '1/-1' }}>No posters available yet.</p>}
          {posters.map((poster, i) => (
            <div key={poster._id || i}
              className={`poster-list-card ${selected?._id === poster._id ? 'selected' : ''}`}
              onClick={() => setSelected(poster)}>
              <div className="poster-thumb">
                {poster.image
                  ? <img src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${poster.image.replace(/\\/g, '/')}`} alt={poster.label || 'Poster'} crossOrigin="anonymous" />
                  : <div className="poster-thumb-emoji-wrap" style={{ background: poster.bg || catBg || '#eee' }}><span>{poster.emoji}</span></div>
                }
                {selected?._id === poster._id && (
                  <div className="poster-check-badge">✓</div>
                )}
              </div>
              <div className="poster-thumb-footer">
                <span>{poster.label || poster.title || 'Poster'}</span>
                <button className="poster-preview-btn">👁 Preview</button>
              </div>
            </div>
          ))}
        </div>

        {selected && (
          <div style={{ textAlign: 'center', margin: '24px 0 40px' }}>
            <button className="poster-create-btn" onClick={handleCreate} disabled={creating}>
              {creating ? <><span className="poster-create-spinner" /> Creating Poster...</> : '🎨 Create Poster'}
            </button>
          </div>
        )}
      </div>

      {creating && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '40px 48px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎨</div>
            <p style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Creating your poster...</p>
            <div style={{ width: 200, height: 4, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden', margin: '0 auto' }}>
              <div style={{ height: '100%', background: 'linear-gradient(90deg,#00C853,#2563eb)', borderRadius: 4, animation: 'cs-progress 1.8s linear forwards' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ──
export default function DigitalPoster() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedCat, setSelectedCat] = useState(searchParams.get('cat') || null);
  const [selectedPoster, setSelectedPoster] = useState(null);
  const myBusiness = useMyBusiness();

  const visibleCategories = categories.filter(cat => {
    if (cat.name === 'Business') return !!myBusiness;
    return true;
  });

  // Back from poster preview → go back to poster list (not browser history)
  if (selectedPoster) {
    return (
      <PosterPreviewPage
        poster={selectedPoster}
        category={selectedCat}
        onBack={() => setSelectedPoster(null)}
      />
    );
  }

  // Back from poster list → go back to category list
  if (selectedCat) {
    const cat = categories.find(c => c.name === selectedCat);
    return (
      <PosterList
        category={selectedCat}
        catBg={cat?.bg}
        onSelect={setSelectedPoster}
        onBack={() => setSelectedCat(null)}
      />
    );
  }

  // Category selection page → back goes to previous route
  return (
    <div className="poster-page">
      <div className="poster-header" style={{ background: 'linear-gradient(135deg,#4ecdc4,#43a047)' }}>
        <div className="container">
          <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
          <h1>Select Poster Category</h1>
        </div>
      </div>
      <div className="container">
        <div className="poster-cat-grid">
          {visibleCategories.map((cat, i) => (
            <div key={i} className="poster-cat-card"
              style={{ background: cat.bg }}
              onClick={() => setSelectedCat(cat.name)}>
              <div className="poster-cat-card-icon">{cat.emoji}</div>
              <span className="poster-cat-card-label">{cat.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
